import {
  isDateTimeTableFilter,
  isLabelsTableFilter,
  isNumberTableFilter,
  isRoutingIpv6TableFilter,
  isRoutingTableFilter,
  parseDisplayTokensToLabelFilters,
} from '@ipfabric/utils';
import { getDateForFilter } from 'components/ApiTable/helpers/getDateForFilter';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import type { AllowedOperator } from 'components/Table/components/SimpleFilter/helpers/filterOperators';
import { getChildFilterField } from 'components/Table/components/SimpleFilter/helpers/getChildFilterField';
import { getFilterFromReadable } from 'components/Table/components/SimpleFilter/helpers/getFilterFromReadable';
import type { ChangeFilterEvent } from 'components/Table/components/SimpleFilter/types/ChangeFilterEvent';
import type { SimpleFilterField } from 'components/Table/components/SimpleFilter/types/SimpleFilterField';
import {
  isValidCidr,
  isValidCidrIpv6,
  isValidIp,
  isValidIpv6,
  isValidMeasureUnit,
  isValidNumber,
} from 'utils/formValidation/validations';

const isFilterValid = (
  filterField: SimpleFilterField,
  newValue: string | string[] | null,
  ruleContext: RuleContext,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable for validation
) => {
  if (Array.isArray(newValue)) {
    // Array is used only by multiselect and it cannot get into invalid state from UI.
    return true;
  }

  if (isLabelsTableFilter(filterField.filter)) {
    if (newValue === null || newValue === '') {
      return true;
    }
    return parseDisplayTokensToLabelFilters(newValue).length > 0;
  }

  const filterValue = getFilterFromReadable(filterField.filter, newValue);

  if (filterValue && (filterValue[1] === '' || filterValue[1] === null)) {
    return false;
  }

  if (filterValue && isNumberTableFilter(filterField.filter)) {
    return isValidNumber(filterValue[1]) || isValidMeasureUnit(filterValue[1]);
  }

  if (filterValue && isRoutingTableFilter(filterField.filter)) {
    const operator = filterValue[0] as AllowedOperator;
    switch (operator) {
      case 'ip':
        return isValidIp(filterValue[1]);
      case 'sect':
      case 'nsect':
      case 'gte':
      case 'gt':
      case 'lte':
      case 'lt':
      case 'eq':
      case 'neq':
        return isValidCidr(filterValue[1]);
      default:
        return true;
    }
  }

  if (filterValue && isRoutingIpv6TableFilter(filterField.filter)) {
    const operator = filterValue[0] as AllowedOperator;
    switch (operator) {
      case 'ip':
        return isValidIpv6(filterValue[1]);
      case 'eq':
      case 'neq':
        return isValidCidrIpv6(filterValue[1]);
      default:
        return true;
    }
  }

  if (filterValue && isDateTimeTableFilter(filterField.filter)) {
    if (filterValue[1] && typeof filterValue[1] === 'string') {
      const parsedDate = getDateForFilter(filterValue[1], ruleContext);
      return parsedDate !== null;
    }
    return false;
  }

  return true;
};
export const updateSimpleFilterFields = (
  simpleFilterFields: Record<string, SimpleFilterField>,
  changeFilterEvent: ChangeFilterEvent,
  ruleContext: RuleContext,
): Record<string, SimpleFilterField> => {
  // Fixed noUncheckedIndexedAccess NIM-19408
  const filterField = simpleFilterFields[changeFilterEvent.column]!;
  const realFilterField = changeFilterEvent.child
    ? getChildFilterField(filterField)
    : filterField;

  return {
    ...simpleFilterFields,
    [changeFilterEvent.column]: {
      ...filterField,
      value: changeFilterEvent.value,
      error: !isFilterValid(
        realFilterField || filterField,
        changeFilterEvent.value,
        ruleContext,
      ),
      child: changeFilterEvent.child,
    },
  };
};
